
<template>
    <div class="container mx-auto">
        <div class="flex justify-center gap-x-10">
            <div class="flex flex-col">
                <h1 class="page-header">CRESiteIQ&trade;</h1>
                <ul class="mt-8 flex whitespace-nowrap lg:flex-col" role="list">
                    <li>
                        <button @click="currentSection = 'metrics'"
                            class="group py-2 pl-2 pr-3 group flex items-center gap-x-3 leading-6 hover:text-green-700 font-medium rounded-md"
                            :class="currentSection == 'metrics' ? 'text-green-600' : 'text-gray-700'">
                            <inline-svg name="filter" classes="w-5 h-5" />
                            Metrics
                        </button>
                    </li>
                    <li>
                        <button @click="currentSection = 'data'"
                            class="group py-2 pl-2 pr-3 group flex items-center gap-x-3 leading-6 hover:text-green-700 font-medium rounded-md"
                            :class="currentSection == 'data' ? 'text-green-600' : 'text-gray-700' ">
                            <inline-svg name="xls" classes="w-5 h-5" />
                            Data
                        </button>
                    </li>
                    <li>
                        <button @click="currentSection = 'poi'"
                            class="group py-2 pl-2 pr-3 group flex items-center gap-x-3 leading-6 hover:text-green-700 font-medium rounded-md"
                            :class="currentSection == 'poi' ? 'text-green-600' : 'text-gray-700' ">
                            <inline-svg name="settings" classes="w-5 h-5" />
                            Manage POI
                        </button>
                    </li>
                </ul>
            </div>
            <div class="flex-1 bg-gray-50">
                <div class="py-8 px-10">
                    <div v-if="currentSection == 'metrics'">
                        <div class="flex items-center gap-x-10 pb-3 border-b border-gray-200">
                            <h2 class="page-header">Metrics</h2>
                            <nav class="flex space-x-4" aria-label="Tabs">
                                <button type="button" @click="currentTab = 'groups'"
                                    class="rounded-md px-3 py-2 text-sm font-medium"
                                    :class="currentTab == 'groups' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'">
                                    Groups
                                </button>
                                <button type="button" @click="currentTab = 'metrics'"
                                    class="rounded-md px-3 py-2 text-sm font-medium"
                                    :class="currentTab == 'metrics' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'">
                                    Metrics
                                </button>
                            </nav>
                        </div>

                        <div v-if="currentTab == 'groups'" class="space-y-10 sm:space-y-14">
                            <div class="mt-5">
                                <div class="flex gap-x-20">
                                    <div class="flex flex-col bg-gray-100 p-5">
                                        <div class="flex items-center gap-x-2">
                                            <label for="selected_group" class="mb-0">Selected Group:</label>
                                            <button type="button" @click="$modal.show('create-metric-group')">
                                                <svg class="w-4 h-4 text-blue-500 hover:text-blue-400 fill-current cursor-pointer"
                                                    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                    <path
                                                        d="M11 9V5H9v4H5v2h4v4h2v-4h4V9h-4zm-1 11a10 10 0 1 1 0-20 10 10 0 0 1 0 20z" />
                                                </svg>
                                            </button>
                                        </div>
                                        <!-- <select name="selected_group" v-model="selectedGroupSlug"
                                            class="form-select mt-2">
                                            <option value="">Select a Metric Group</option>
                                            <option v-for="group in sortedGroups" :value="group.slug">{{ group.label }}
                                            </option>
                                        </select> -->
                                        <SearchableSelect
                                            :items="mappedGroups"
                                            :initialSelectionId="selectedGroupSlug"
                                            @update:selected-id="updateSelectedGroup"
                                        />
                                    </div>
                                    <div>
                                        <div v-if="selectedGroupSlug != ''">
                                            <h2 class="text-xl font-bold">{{ selectedGroup.label }}</h2>
                                            <div class="mt-5 flex items-center justify-center gap-x-5">
                                                <div class="flex flex-col">
                                                    <label for="available_metrics" class="mb-0">Available
                                                        Metrics:</label>
                                                    <div class="w-64">
                                                        <div @click="isOpen = !isOpen" class="cursor-pointer p-2 border rounded-md">
                                                            <span v-if="creSiteIQ.selectedMetrics.length">
                                                                {{ creSiteIQ.selectedMetrics.length }} metric(s) selected
                                                            </span>
                                                            <span v-else>Select metrics...</span>
                                                        </div>

                                                        <div v-show="isOpen" >
                                                            <ul class="max-h-96 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm" id="options" role="listbox">
                                                                <li class="py-2">
                                                                    <div class="relative px-4 cursor-default select-none text-gray-900">
                                                                        <span class="block truncate">
                                                                            <input type="search" v-model="searchCriteria" class="block w-full p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50" placeholder="Search..." />
                                                                        </span>
                                                                        <span class="absolute inset-y-0 left-0 flex items-center pl-8 text-indigo-600">
                                                                            <inline-svg name="search" classes="w-4 h-4 text-gray-500 dark:text-gray-400"></inline-svg>
                                                                        </span>
                                                                    </div>
                                                                </li>
                                                                <li
                                                                    v-for="metric in availableMetrics"
                                                                    :key="metric.slug"
                                                                    @click="toggleMetric(metric)"
                                                                    class="relative cursor-default select-none py-2 pl-6 pr-4 text-gray-900 hover:bg-gray-50 hover:cursor-pointer"
                                                                >
                                                                    <span :title="metric.label" class="block truncate pl-2" :class="{ 'font-semibold': isMetricSelected(metric.slug) }">
                                                                        {{ metric.label }}
                                                                    </span>

                                                                    <span v-if="isMetricSelected(metric.slug)" class="absolute inset-y-0 left-0 flex items-center pl-1.5 text-indigo-600">
                                                                        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                                                                            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                                                                        </svg>
                                                                    </span>
                                                                </li>

                                                                
                                                                
                                                            </ul>

                                                        
                                                        </div>
                                                    </div>


                                                    <!-- <select @dblclick="addSelectedMetrics" id="available_metrics" style="overflow:scroll;"
                                                        name="available_metrics" class=" h-96 form-select " multiple>
                                                        <option
                                                            v-for="metric in availableMetrics"
                                                            class="p-1"
                                                            :class="metric.is_enabled ? 'text-green-700': 'text-red-600'"
                                                            :value="metric.slug"
                                                        >
                                                            {{ metric.label }}
                                                        </option>
                                                    </select> -->
                                                </div>
                                                <div class="flex flex-col gap-y-3">
                                                    <button type="button" @click="removeSelectedMetrics"
                                                        class="px-2 py-1 border border-gray-400 rounded-md text-sm text-gray-600 hover:text-gray-500 font-black">
                                                        &lt; </button>
                                                            <button type="button" @click="addSelectedMetrics"
                                                                class="px-2 py-1 border border-gray-400 rounded-md text-sm text-gray-600 hover:text-gray-500 font-black">
                                                                >
                                                            </button>
                                                </div>
                                                <div class="flex flex-col">
                                                    <label for="selected_metrics" class="mb-0">{{ selectedGroup.label }}
                                                        Metrics:</label>

                                                        <div class="w-64" style="height: 425px;">
                                                        <div @click="isOpen = !isOpen" class="cursor-pointer p-2 border rounded-md">
                                                            <span v-if="creSiteIQ.selectedMetrics.length">
                                                                {{ creSiteIQ.selectedMetrics.length }} metric(s) selected
                                                            </span>
                                                            <span v-else>Select metrics...</span>
                                                        </div>

                                                        <div v-show="isOpen" >
                                                            <ul class="max-h-96 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm" id="options" role="listbox">
                                                                <li class="py-2">
                                                                    <div class="relative px-4 cursor-default select-none text-gray-900">
                                                                        <span class="block truncate">
                                                                            <input type="search" v-model="searchCriteriaMetric" class="block w-full p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50" placeholder="Search..." />
                                                                        </span>
                                                                        <span class="absolute inset-y-0 left-0 flex items-center pl-8 text-indigo-600">
                                                                            <inline-svg name="search" classes="w-4 h-4 text-gray-500 dark:text-gray-400"></inline-svg>
                                                                        </span>
                                                                    </div>
                                                                </li>
                                                                <li
                                                                    v-for="metric in selectedMetrics"
                                                                    :key="metric.slug"
                                                                    @click="toggleMetric(metric)"
                                                                    class="relative cursor-default select-none py-2 pl-6 pr-4 text-gray-900 hover:bg-gray-50 hover:cursor-pointer"
                                                                >
                                                                    <span :title="metric.label" class="block truncate pl-2" :class="{ 'font-semibold': isMetricSelected(metric.slug) }">
                                                                        {{ metric.label }}
                                                                    </span>

                                                                    <span v-if="isMetricSelected(metric.slug)" class="absolute inset-y-0 left-0 flex items-center pl-1.5 text-indigo-600">
                                                                        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                                                                            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
                                                                        </svg>
                                                                    </span>
                                                                </li>

                                                                
                                                                
                                                            </ul>

                                                        
                                                        </div>
                                                    </div>
                                                    <!-- <select @dblclick="removeSelectedMetrics" id="selected_metrics"
                                                        name="selected_metrics" class="w-64 h-96 form-select" multiple>
                                                        <option
                                                            v-for="metric in selectedMetrics"
                                                            class="p-1"
                                                            :class="metric.is_enabled ? 'text-green-700': 'text-red-600'"
                                                            :value="metric.slug">
                                                                {{ metric.label }}
                                                        </option>
                                                    </select> -->
                                                </div>
                                            </div>
                                            <button class="mt-10 btn btn-red" @click="confirmDeleteGroup">
                                                <span>Delete Group</span>
                                            </button>
                                        </div>

                                        <div v-else class="text-gray-700 font-medium">
                                            Select a Metric Group
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="currentTab == 'metrics'" class="space-y-10 sm:space-y-14">
                            <div v-if="uploadSuccessful" class="alert green text-sm" v-text="uploadMessage">
                                    </div>
                                    <div v-if="errors.upload" class="alert red">
                                        <span class="text-sm font-semibold">The following errors have occurred:</span>
                                        <ul class="ml-6 mt-1 list-disc text-sm font-normal">
                                            <li v-for="error in errors.upload" :key="error">{{ error }}</li>
                                        </ul>
                                    </div>
                            <div class="mt-5">
                                
                                <div class="flex gap-x-20">
                                   
                                    <div class="flex flex-col bg-gray-100 p-5">
                                        <div class="flex items-center gap-x-2">
                                            <label for="selected_group" class="mb-0">Selected Metric:</label>
                                            <button type="button" @click="$modal.show('create-metric')">
                                                <svg class="w-4 h-4 text-blue-500 hover:text-blue-400 fill-current cursor-pointer"
                                                    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                    <path
                                                        d="M11 9V5H9v4H5v2h4v4h2v-4h4V9h-4zm-1 11a10 10 0 1 1 0-20 10 10 0 0 1 0 20z" />
                                                </svg>
                                            </button>
                                        </div>
                                        <!-- <select name="selected_metric" v-model="selectedMetricId"
                                            class="form-select mt-2 js-example-basic-single">
                                            <option value="">Select a Metric</option>
                                            <option v-for="metric in sortedMetrics" :value="metric.id">
                                                {{ metric.label | truncate(30) }}
                                            </option>
                                        </select> -->
                                        
                                        <div>
                                            <SearchableSelect
                                                :items="sortedMetrics"  
                                                :initialSelectionId="selectedMetricId"
                                                @update:selected-id="selectedMetricId = $event"
                                            />

                                        </div>

                                    </div>
                                    <div class="w-full">
                                        <div v-if="selectedMetricId != ''" class="mb-10">
                                            <h2 class="text-xl font-bold">{{ selectedMetric.label }}</h2>
                                            <div class="mt-5 flex justify-between gap-x-10">
                                                <div class="grid grid-cols-2 gap-x-10 gap-y-6">
                                                    <div class="flex flex-col">
                                                        <label for="metric_label">Slug (used when importing):</label>
                                                        <input v-model="selectedMetric.slug" name="metric_slug"
                                                            type="text" class="form-input w-60"
                                                            placeholder="i.e. age_median" />
                                                        <div v-if="errors.slug" class="form-error">{{ errors.slug[0] }}
                                                        </div>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <label for="metric_label">Label:</label>
                                                        <input v-model="selectedMetric.label" name="metric_label"
                                                            type="text" class="form-input w-60" />
                                                        <div v-if="errors.label" class="form-error">{{ errors.label[0]
                                                            }}</div>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <label for="metric_label">Group:</label>
                                                        <select name="selected_group" v-model="selectedMetric.group_id"
                                                            class="form-select mt-0">
                                                            <option value="">Select a Metric Group</option>
                                                            <option v-for="group in sortedGroups" :key="group.id" :value="group.id">{{
                                                                group.label }}</option>
                                                        </select>
                                                        <div v-if="errors.group" class="form-error">{{ errors.group[0]
                                                            }}</div>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <label for="metric_label">Type:</label>
                                                        <select name="metric_type" v-model="selectedMetric.type"
                                                            class="form-select mt-0">
                                                            <option value="">Select a Metric Type</option>
                                                            <option value="int">Integer</option>
                                                            <option value="currency">Currency</option>
                                                            <option value="percentage">Percentage</option>
                                                            <option value="bool">Boolean</option>
                                                        </select>
                                                        <div v-if="errors.type" class="form-error">{{ errors.type[0] }}
                                                        </div>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <label for="metric_label">Min Value:</label>
                                                        <input v-model="selectedMetric.min_value" name="min_value"
                                                            type="text" class="form-input w-60" />
                                                        <div v-if="errors.min_value" class="form-error">{{
                                                            errors.min_value[0] }}</div>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <label for="metric_label">Max Value:</label>
                                                        <input v-model="selectedMetric.max_value" name="max_value"
                                                            type="text" class="form-input w-60" />
                                                        <div v-if="errors.max_value" class="form-error">{{
                                                            errors.max_value[0] }}</div>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <label for="metric_granularity">Granularity:</label>
                                                        <select name="metric_granularity" v-model="selectedMetric.granularity"
                                                            class="form-select mt-0">
                                                            <option value="">Select a Granularity</option>
                                                            <option value="state">State</option>
                                                            <option value="zip">Zip</option>
                                                        </select>
                                                        <div v-if="errors.granularity" class="form-error">{{
                                                            errors.granularity[0] }}</div>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <label for="metric_default_filter_type">Filter Type:</label>
                                                        <select name="metric_default_filter_type" v-model="selectedMetric.default_filter_type"
                                                            class="form-select mt-0">
                                                            <option value="">Select a Filter Type</option>
                                                            <option value="lt">Less Than</option>
                                                            <option value="gt">Greater Than</option>
                                                            <option value="range">Range</option>
                                                        </select>
                                                        <div v-if="errors.default_filter_type" class="form-error">{{
                                                            errors.default_filter_type[0] }}</div>
                                                    </div>
                                                </div>
                                                <div class="flex flex-col gap-y-2 text-sm">
                                                    <div class="flex items-center justify-end gap-x-2">
                                                        Is Enabled
                                                        <toggle-switch-small v-model="selectedMetric.is_enabled"
                                                            :value="selectedMetric.is_enabled" name="is_enabled">
                                                        </toggle-switch-small>
                                                    </div>
                                                    <div class="flex items-center justify-end gap-x-2">
                                                        Higher is Better
                                                        <toggle-switch-small v-model="selectedMetric.higher_is_better"
                                                            :value="selectedMetric.higher_is_better"
                                                            name="higher_is_better">
                                                        </toggle-switch-small>
                                                    </div>
                                                </div>
                                            </div>

                                            <button class="mt-10 btn btn-green" @click="saveMetric">
                                                <span>Save</span>
                                            </button>
                                        </div>

                                        <div v-else class="text-gray-700 font-medium">
                                            Select a Metric
                                        </div>
                                    </div>
                                </div>
                                 <div v-if="selectedMetricId != ''" class="my-10 w-full border-dashed border-2 border-gray-200 rounded-md p-5">
                                    <h2 class="text-lg font-bold">{{ selectedMetric.label }} Data</h2>
                                    <div class="mt-5">
                                        <div v-if="selectedMetric.data?.length" class="flex flex-col">
                                            <table class="w-full mb-6">
                                                <thead class="cursor-pointer">
                                                    <tr>
                                                        <th class="pl-5">{{ selectedMetric.granularity == 'state' ? 'State' : 'Zip' }}</th>
                                                        <th class="text-right">Value</th>
                                                        <th class="pl-5">From Dataset</th>
                                                        <th>Uploaded By</th>
                                                        <th>Uploaded Date</th>
                                                    </tr>
                                                </thead>
                                                <paginate name="dataset" :list="selectedMetric.data" :per="60" tag="tbody">
                                                    <tr v-for="(row, index) in paginated('dataset')" :key="index">
                                                        <td class="pl-5">
                                                            {{ selectedMetric.granularity == 'state' ? row.area.state : row.area.zip }}
                                                        </td>
                                                        <td class="text-right">
                                                            {{ row.value }}
                                                        </td>
                                                        <td class="pl-5">
                                                            {{ row.dataset?.file_name || '-' }}
                                                        </td>
                                                        <td>
                                                            {{ row.dataset?.uploader?.name || '-' }}
                                                        </td>
                                                        <td>
                                                            {{ row.dataset?.created_at || '-' }}
                                                        </td>
                                                    </tr>
                                                </paginate>
                                            </table>
                                            <paginate-links for="dataset"  :simple="{ prev: 'Back', next: 'Next' }"></paginate-links>
                                        </div>
                                        <div v-else class="text-center">
                                            <div v-if="loading" class="mx-auto">
                                                <inline-svg name="loading" />
                                            </div>
                                            <button
                                                v-else
                                                @click="loadMetricData"
                                                type="button"
                                                class="px-5 py-3 shadow-sm border border-gray-200 bg-gray-100 font-semibold hover:bg-gray-300 rounded-md text-sm">
                                                    Load Data
                                            </button>

                                        </div>
                                    </div>
                                 </div>
                            </div>
                            <div class="mt-5">
                                <div class='bg-gray-100 p-5'>
                                    <h5 >Matrix Bulk Upload</h5>
                                    <div class="max-w-md">
                                        <input @change="onUploadFileChanged($event)" name="uploaded_file"
                                            type="file" 
                                            accept="text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                            class="w-full text-slate-500 font-medium text-sm bg-white border file:cursor-pointer cursor-pointer file:border-0 file:py-3 file:px-4 file:mr-4 file:bg-gray-100 file:hover:bg-gray-200 file:text-slate-500 rounded" />
                                        <p class="text-xs text-slate-500 mt-2">Only CSV files are allowed</p>
                                        
                                        <button class="mt-5 btn btn-green max-w-fit"
                                         @click="uploadMatrixData"
                                         :disabled="loading"
                                        >
                                            <span v-if="loading">
                                                <inline-svg name="loading-button"></inline-svg>                                
                                            </span>
                                            <span v-if="! loading">Upload</span>
                                        </button>
                                        <button class="mt-5 btn btn-blue max-w-fit"
                                            @click="downloadTemplate"
                                            :disabled="loading"
                                            title="Download Template"
                                        >
                                            <span v-if="loading">
                                                <inline-svg name="loading-button"></inline-svg>                                
                                            </span>
                                            <span v-if="! loading">Download Template</span>

                                        </button>
                                        
                                    </div>
                                    
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="currentSection == 'data'">
                        <div class="flex items-center gap-x-10 pb-3 border-b border-gray-200">
                            <h2 class="page-header">Data</h2>
                            <nav class="flex space-x-4" aria-label="Tabs">
                                <button type="button" @click="currentTab = 'uploaded-datasets'"
                                    class="rounded-md px-3 py-2 text-sm font-medium"
                                    :class="currentTab == 'uploaded-datasets' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'">
                                    Uploaded Datasets
                                </button>
                                <button type="button" @click="currentTab = 'upload'"
                                    class="rounded-md px-3 py-2 text-sm font-medium"
                                    :class="currentTab == 'upload' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'">
                                    Upload Data
                                </button>
                            </nav>
                        </div>

                        <div class="py-3">
                            <div v-if="currentTab == 'uploaded-datasets'">
                                <div class="flex flex-col gap-y-5 bg-white rounded-md p-5">
                                    <h3 class="module-header">Datasets</h3>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th class="pl-4">File Name</th>
                                                <th>Uploaded By</th>
                                                <th>Upload Date</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200">
                                            <tr v-for="dataset in datasets" :key="dataset.id" class="hover:bg-gray-50">
                                                <td class="pl-4">{{ dataset.file_name }}</td>
                                                <td>{{ dataset.uploader.name }}</td>
                                                <td>{{ dataset.created_at | date }}</td>
                                                <td>
                                                    <a :href="dataset.download_url" title="Download Dataset" class="cursor-pointer">
                                                        <inline-svg name="download"></inline-svg>
                                                    </a>
                                                </td>
                                            </tr>
                                            <tr v-if="datasets.length === 0">
                                                <td colspan="3" class="pt-5 text-center">No datasets uploaded yet</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div v-if="currentTab == 'upload'" class="flex flex-col gap-y-10">
                                <div class="flex flex-col gap-y-5 bg-white rounded-md p-5">
                                    <h3 class="module-header">Upload Data</h3>
                                    <div v-if="errors.upload" class="alert red">
                                        <span class="text-sm font-semibold">The following errors have occurred:</span>
                                        <ul class="ml-6 mt-1 list-disc text-sm font-normal">
                                            <li v-for="error in errors.upload" :key="error">{{ error }}</li>
                                        </ul>
                                    </div>
                                    <div v-if="uploadSuccessful" class="alert green text-sm" v-text="uploadMessage">
                                    </div>
                                    <fieldset>
                                        <span class="text-sm/6 font-semibold text-gray-900">1. Type of data</span>
                                        <div class="mt-2 grid grid-cols-2 gap-x-4">
                                            <label
                                                class="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none"
                                                :class="uploadedDataType == 'state' ? 'border-indigo-600 ring-2 ring-indigo-600' : 'border-gray-300'">
                                                <input @click="uploadedDataType = 'state'" type="radio"
                                                    name="uploaded_data_type" value="state" class="sr-only">
                                                <span class="flex flex-1">
                                                    <span class="flex flex-col">
                                                        <span class="block text-sm font-medium text-gray-900">State
                                                            Data</span>
                                                        <span
                                                            class="mt-1 flex items-center text-sm text-gray-500 gap-x-1">Required
                                                            column: <strong>state</strong> <span class="text-xs">(i.e.
                                                                NY)</span></span>
                                                    </span>
                                                </span>
                                                <svg v-if="uploadedDataType == 'state'" class="w-5 h-5 text-indigo-600"
                                                    viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
                                                    data-slot="icon">
                                                    <path fill-rule="evenodd"
                                                        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                                        clip-rule="evenodd" />
                                                </svg>
                                                <span class="pointer-events-none absolute -inset-px rounded-lg border-1"
                                                    aria-hidden="true"
                                                    :class="uploadedDataType == 'state' ? 'border border-indigo-600' : 'border-2 border-transparent'">
                                                </span>
                                            </label>
                                            <label
                                                class="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none"
                                                :class="uploadedDataType == 'zip' ? 'border-indigo-600 ring-2 ring-indigo-600' : 'border-gray-300'">
                                                <input @click="uploadedDataType = 'zip'" type="radio"
                                                    name="uploaded_data_type" value="zip" class="sr-only">
                                                <span class="flex flex-1">
                                                    <span class="flex flex-col">
                                                        <span class="block text-sm font-medium text-gray-900">Zip
                                                            Data</span>
                                                        <span
                                                            class="mt-1 flex items-center text-sm text-gray-500 gap-x-1">Required
                                                            column: <strong>zip</strong> <span class="text-xs">(i.e.
                                                                34202)</span></span>
                                                    </span>
                                                </span>
                                                <svg v-if="uploadedDataType == 'zip'" class="w-5 h-5 text-indigo-600"
                                                    viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
                                                    data-slot="icon">
                                                    <path fill-rule="evenodd"
                                                        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                                        clip-rule="evenodd" />
                                                </svg>
                                                <span class="pointer-events-none absolute -inset-px rounded-lg border-1"
                                                    aria-hidden="true"
                                                    :class="uploadedDataType == 'zip' ? 'border border-indigo-600' : 'border-2 border-transparent'">
                                                </span>
                                            </label>
                                        </div>
                                    </fieldset>

                                    <div class="mt-5 flex flex-col gap-y-2">
                                        <span class="text-sm/6 font-semibold text-gray-900">2. File to Upload</span>
                                        <div class="max-w-md">
                                            <input @change="onUploadFileChanged($event)" name="uploaded_file"
                                                type="file" accept="text/csv"
                                                class="w-full text-slate-500 font-medium text-sm bg-white border file:cursor-pointer cursor-pointer file:border-0 file:py-3 file:px-4 file:mr-4 file:bg-gray-100 file:hover:bg-gray-200 file:text-slate-500 rounded" />
                                            <p class="text-xs text-slate-500 mt-2">Only CSV files are allowed</p>
                                        </div>
                                    </div>
                                    <div v-if="isUploading || isProcessing || loading">
                                        <p>Upload Progress: {{ uploadProgress.toFixed(0) }}%</p>
                                        <progress :value="uploadProgress" max="100"></progress>
                                        <span v-if="isProcessing"> (Processing on server...)</span>
                                    </div>

                                    <button class="mt-5 btn btn-green max-w-fit"
                                        @click="uploadData"
                                        :disabled="loading"
                                    >
                                        <inline-svg v-if="loading" name="loading-button"></inline-svg>
                                        <span v-if="! loading">Upload</span>
                                    </button>
                                </div>
                                <div class="flex flex-col gap-y-5 py-5">
                                    <h3 class="module-header">Metric Reference</h3>
                                    <table v-if="uploadedDataType" class="min-w-[400px]">
                                        <thead>
                                            <tr>
                                                <th class="pl-4">Metric</th>
                                                <th>Column Header</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(metric, metricIndex) in filteredMetrics" :key="metricIndex">
                                                <td class="pl-4">{{ metric.label }}</td>
                                                <td>{{ metric.slug }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div v-else class="text-gray-700 font-medium">Select a data type</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="currentSection == 'poi'">
                        
                        <div class="flex items-center gap-x-10 pb-3 border-b border-gray-200">
                            <h2 class="page-header">Manage POI</h2>
                        </div>
                        <div class="flex flex-col bg-gray-100 p-5">
                            <div class="flex items-center gap-x-2">
                            <label for="selected_group" class="mb-0">Show POI Section:</label>
                            
                        </div>
                        <div class="relative ml-5 p-1 rounded-lg items-center sm:flex" style="width:90px;">
                            <div>
						        <toggle-switch v-model="poiEnabled" @input="updatePoiSetting"></toggle-switch>
					        </div>
                        </div>
                        
                    </div>
                    </div>
                </div>
            </div>
        </div>

        <modal name="create-metric-group" width="500px" height="auto" :clickToClose="true" :scrollable="true"
            draggable=".modal-header">
            <div class="flex flex-col justify-between">
                <div class="w-full flex flex-col">
                    <div class="modal-header flex items-center justify-between cursor-move">
                        <h1 class="flex-1 text-xl">Create Metric Group</h1>
                        <div @click="$modal.hide('create-metric-group')">
                            <inline-svg name="close"
                                classes="w-4 h-4 text-white fill-current cursor-pointer"></inline-svg>
                        </div>
                    </div>
                    <div class="w-full flex p-6">
                        <div class="flex flex-col gap-y-1">
                            <label for="metric_group_label" class="mb-0">Group Name:</label>
                            <input v-model="newMetricGroupLabel" name="metric_group_label" type="text"
                                class="form-input w-64" autofocus />
                            <div v-if="errors.label" class="form-error">{{ errors.label[0] }}</div>
                        </div>
                    </div>
                </div>

                <div class="bg-gray-100 p-4">
                    <div class="text-right">
                        <button class="btn btn-secondary mr-2"
                            @click="$modal.hide('create-metric-group')">Close</button>
                        <button class="btn btn-green" @click="createMetricGroup()">
                            <span>Save</span>
                        </button>
                    </div>
                </div>
            </div>
        </modal>

        <modal name="create-metric" width="600px" height="auto" :clickToClose="true" :scrollable="true"
            draggable=".modal-header">
            <div class="flex flex-col justify-between">
                <div class="w-full flex flex-col">
                    <div class="modal-header flex items-center justify-between cursor-move">
                        <h1 class="flex-1 text-xl">Create Metric</h1>
                        <div @click="$modal.hide('create-metric')">
                            <inline-svg name="close"
                                classes="w-4 h-4 text-white fill-current cursor-pointer"></inline-svg>
                        </div>
                    </div>
                    <div class="w-full flex p-6">
                        <div class="mt-5 grid grid-cols-3 gap-x-10 gap-y-3">
                            <div class="flex flex-col col-span-2">
                                <label for="metric_label">Label:</label>
                                <input v-model="newMetric.label" name="metric_label" type="text"
                                    class="form-input w-full" />
                                <div v-if="errors.label" class="form-error">{{ errors.label[0] }}</div>
                            </div>
                            <div class="flex flex-col gap-y-2 text-sm">
                                <div class="flex items-center justify-end gap-x-2">
                                    Is Enabled
                                    <toggle-switch-small v-model="newMetric.is_enabled" :value="newMetric.is_enabled"
                                        name="is_enabled">
                                    </toggle-switch-small>
                                </div>
                                <div class="flex items-center justify-end gap-x-2">
                                    Higher is Better
                                    <toggle-switch-small v-model="newMetric.higher_is_better"
                                        :value="newMetric.higher_is_better" name="higher_is_better">
                                    </toggle-switch-small>
                                </div>
                            </div>
                            <div class="flex flex-col">
                                <label for="metric_label">Group:</label>
                                <select name="selected_group" v-model="newMetric.group_id" class="form-select mt-0">
                                    <option value="">Select a Metric Group</option>
                                    <option v-for="group in sortedGroups" :key="group.id" :value="group.id">{{ group.label }}</option>
                                </select>
                                <div v-if="errors.group" class="form-error">{{ errors.group[0] }}</div>
                            </div>
                            <div class="flex flex-col">
                                <label for="metric_label">Type:</label>
                                <select name="metric_type" v-model="newMetric.type" class="form-select mt-0">
                                    <option value="">Select a Metric Type</option>
                                    <option value="int">Integer</option>
                                    <option value="currency">Currency</option>
                                    <option value="percentage">Percentage</option>
                                    <option value="bool">Boolean</option>
                                </select>
                                <div v-if="errors.type" class="form-error">{{ errors.type[0] }}</div>
                            </div>
                            <div></div>
                            <div class="flex flex-col">
                                <label for="metric_granularity">Granularity:</label>
                                <select name="metric_granularity" v-model="newMetric.granularity"
                                    class="form-select mt-0">
                                    <option value="">Select a Granularity</option>
                                    <option value="state">State</option>
                                    <option value="zip">Zip</option>
                                </select>
                                <div v-if="errors.granularity" class="form-error">{{
                                    errors.granularity[0] }}</div>
                            </div>                            
                            <div class="flex flex-col">
                                <label for="metric_default_filter_type">Filter Type:</label>
                                <select name="metric_default_filter_type" v-model="newMetric.default_filter_type"
                                    class="form-select mt-0">
                                    <option value="">Select a Filter Type</option>
                                    <option value="lt">Less Than</option>
                                    <option value="gt">Greater Than</option>
                                    <option value="range">Range</option>
                                </select>
                                <div v-if="errors.default_filter_type" class="form-error">{{
                                    errors.default_filter_type[0] }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-gray-100 p-4">
                    <div class="text-right">
                        <button class="btn btn-secondary mr-2" @click="$modal.hide('create-metric')">Close</button>
                        <button class="btn btn-green" @click="createMetric()">
                            <span>Save</span>
                        </button>
                    </div>
                </div>
            </div>
        </modal>

        <notifications classes="notification" position="bottom right"></notifications>
    </div>
</template>


<script>
    import SearchableSelect from './SearchableSelect.vue';
    export default {
        name: 'CreSiteIqAdmin',
        filters: {
            truncate(text, length) {
            if (text.length > length) {
                return text.substring(0, length) + '...';
            }
            return text;
            }
        },
        components: {
            SearchableSelect
        },

        props: {
            allDatasets: {
                type: Array,
                required: true
            },
            allMetricsData: {
                type: Array,
                required: true
            },
            metricGroups: {
                type: Array,
                required: true
            },
            
        },

        data() {
            return {
                paginate: ['dataset'],
                datasets: this.allDatasets,
                allMetrics: this.allMetricsData,
                groups: this.metricGroups,
                currentSection: 'metrics',
                currentTab: 'metrics',
                selectedGroupSlug: '',
                selectedMetricId: '',
                newMetricGroupLabel: null,
                newMetric: {},
                uploadSuccessful: false,
                uploadMessage: null,
                uploadedDataType: null,
                uploadedFile: null,
                matrixUploadedFile: null,
                loading: false,
                errors: {},
                poiEnabled:this.poiEnabled,
                uploadProgress: 0,
                isUploadingFile: false,
                isProcessingOnServer: false,
                isUploading: false,     // New: true when file is being sent
                isProcessing: false,
                isOpen: true,
                searchCriteria: '',
                searchCriteriaMetric: '',
                creSiteIQ: {
                    selectedMetrics: [], // This is the array where selected metrics will be stored
                },

            }
        },

        watch: {
            currentSection(newVal) {
                if (newVal == 'metrics') {
                    this.currentTab = 'groups';
                } else if (newVal == 'data') {
                    this.currentTab = 'uploaded-datasets';
                }
            },

            sortedMetrics: {
                immediate: true,
                handler(newMetrics) {
                    // If selectedMetricId is pre-set and exists in newMetrics, ensure it's valid
                    if (this.selectedMetricId && !newMetrics.some(m => m.id === this.selectedMetricId)) {
                        this.selectedMetricId = ''; // Clear if pre-selected ID is no longer valid
                    }
                }
            },
            // Watch for changes in selectedMetricId and potentially do something
            selectedMetricId(newId, oldId) {
                console.log('Metric ID changed from', oldId, 'to', newId);
                // You can emit an event here if this component is part of a larger system
                // this.$emit('metric-selected', newId);
            }


        },

        computed: {
            mappedGroups() {
                // The 'id' for SearchableSelect will be the 'slug' from your groups data
                return this.sortedGroups.map(group => ({
                    id: group.slug,
                    label: group.label,
                }));
            },


            sortedGroups() {
                return _.sortBy(this.groups, 'slug');
            },

            selectedGroup() {
                console.log(this.selectedGroupSlug)
                return _.find(this.groups, ['slug', this.selectedGroupSlug]);
            },

            sortedMetrics() {
                return _.sortBy(this.allMetrics, 'label');
            },

            filteredMetrics() {
                return _.sortBy(
                    _.filter(this.allMetrics, metric => metric.granularity == this.uploadedDataType),
                    'label'
                );
            },

            selectedMetric() {
                return _.find(this.allMetrics, ['id', this.selectedMetricId]);
            },

            // availableMetrics() {
               
            //     let filteredMetrics = _.filter(this.allMetrics, metric => {
            //        return !_.find(this.selectedMetrics, ['slug', metric.slug]);

            //     });

            //     return _.sortBy(filteredMetrics, ['label']);
            // },
            availableMetrics() {
            if (!this.allMetrics) {
                return [];
            }

            let filteredMetrics = _.filter(this.allMetrics, metric => {
                   return !_.find(this.selectedMetrics, ['slug', metric.slug]);

                });

            // Apply the search filter if searchCriteria exists
            if (this.searchCriteria) {
                const searchTerm = this.searchCriteria.toLowerCase();
                filteredMetrics = filteredMetrics.filter(metric => {
                    return (
                        (metric.name && metric.name.toLowerCase().includes(searchTerm)) ||
                        (metric.label && metric.label.toLowerCase().includes(searchTerm)) ||
                        (metric.slug && metric.slug.toLowerCase().includes(searchTerm))
                    );
                });
            }

            // Sort the final list by label
            return _.sortBy(filteredMetrics, 'label');
        },

            availableStates() {
                if (this.searchCriteria == null) {
                    return this.allMetrics;
                }

                return _.filter(this.allMetrics, (metric) => {
                    return metric.name?.toLowerCase().includes(this.searchCriteria.toLowerCase());
                });
            },
            availableMetrics2() {
            if (!this.allMetrics) {
                return [];
            }

            let filteredMetrics = this.allMetrics;

            // Apply the search filter if searchCriteria exists
            if (this.searchCriteria) {
                const searchTerm = this.searchCriteria.toLowerCase();
                filteredMetrics = filteredMetrics.filter(metric => {
                    return (
                        (metric.name && metric.name.toLowerCase().includes(searchTerm)) ||
                        (metric.label && metric.label.toLowerCase().includes(searchTerm)) ||
                        (metric.slug && metric.slug.toLowerCase().includes(searchTerm))
                    );
                });
            }

            // Sort the final list by label
            return _.sortBy(filteredMetrics, 'label');
        },



            // selectedMetrics() {
            //     let filteredMetrics = _.filter(this.allMetrics, metric => {
            //        return !_.find(this.selectedGroup.metrics, ['slug', metric.slug]);

            //     });

            //     if (this.searchCriteriaMetric) {

            //     const searchTerm = this.searchCriteriaMetric.toLowerCase();
            //     filteredMetrics = filteredMetrics.filter(metric => {
            //         return (
            //             (metric.name && metric.name.toLowerCase().includes(searchTerm)) ||
            //             (metric.label && metric.label.toLowerCase().includes(searchTerm)) ||
            //             (metric.slug && metric.slug.toLowerCase().includes(searchTerm))
            //         );
            //     });
            //     console.log(filteredMetrics,'searchCriteriaMetric');

            // }
            //     return _.sortBy(this.selectedGroup?.metrics || [], ['label']);
            // },
            selectedMetrics() { // Keep the name 'selectedMetrics' as per your HTML
                // Ensure selectedGroup and its metrics exist before proceeding
                if (!this.selectedGroup || !this.selectedGroup.metrics) {
                    return [];
                }

                let metricsToDisplay = [...this.selectedGroup.metrics]; // Start with the actual selected metrics

                // Apply search criteria if it exists
                if (this.searchCriteriaMetric) {
                    const searchTerm = this.searchCriteriaMetric.toLowerCase();
                    metricsToDisplay = metricsToDisplay.filter(metric => {
                        return (
                            (metric.name && metric.name.toLowerCase().includes(searchTerm)) ||
                            (metric.label && metric.label.toLowerCase().includes(searchTerm)) ||
                            (metric.slug && metric.slug.toLowerCase().includes(searchTerm))
                        );
                    });
                    console.log(metricsToDisplay, 'filtered by searchCriteriaMetric');
                }

                // Sort the final list
                return _.sortBy(metricsToDisplay, 'label');
            },

            


            


        },

        methods: {
            toggleMetric(metric) {
            const isSelected = this.isMetricSelected(metric.slug);
            if (isSelected) {
                // If it's already selected, remove it
                this.creSiteIQ.selectedMetrics = this.creSiteIQ.selectedMetrics.filter(
                    m => m.slug !== metric.slug
                );
            } else {
                // If it's not selected, add it
                this.creSiteIQ.selectedMetrics.push(metric);
            }
        },
        isMetricSelected(slug) {
            return this.creSiteIQ.selectedMetrics.some(metric => metric.slug === slug);
        },



            updateSelectedGroup(newSlug) {
                this.selectedGroupSlug = newSlug;
                console.log('Group slug updated to:', newSlug);
            },

            // API Calls
            async downloadTemplate() {
                try {
                    const response = await axios.get('/admin/cresiteiq/download-template', {
                        responseType: 'blob',
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'template.csv');
                    document.body.appendChild(link);
                    link.click();
                } catch (error) {
                    console.error(error);
                }
            },

            async createMetricGroup() {
                this.errors = {};
                try {
                    const response = await axios.post('/admin/cresiteiq/metric-group', {
                        label: this.newMetricGroupLabel
                    });
                    this.updateState(response.data.metrics, response.data.groups);
                    this.selectedGroupSlug = response.data.createdGroup.slug;
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: 'Metric Group Created'
                    });
                    this.$modal.hide('create-metric-group');
                } catch (error) {
                    this.errors = error.response.data.errors;
                }
            },

            async saveMetricGroup() {
                this.errors = {};
                try {
                    const response = await axios.patch(`/admin/cresiteiq/metric-group/${this.selectedGroup.id}`, this.selectedGroup);
                    this.updateState(response.data.metrics, response.data.groups);
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: 'Metric Group Updated'
                    });
                } catch (error) {
                    this.errors = error.response.data.errors;
                }
            },

            async createMetric() {
                this.errors = {};
                try {
                    const response = await axios.post('/admin/cresiteiq/metric', this.newMetric);
                    this.newMetric = {};
                    this.updateState(response.data.metrics, response.data.groups);
                    this.selectedMetricId = response.data.createdMetric.id;
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: 'Metric Created'
                    });
                    this.$modal.hide('create-metric');
                } catch (error) {
                    this.errors = error.response.data.errors;
                }
            },

            async saveMetric() {
                this.errors = {};
                try {
                    const response = await axios.patch(`/admin/cresiteiq/metric/${this.selectedMetric.id}`, this.selectedMetric);
                    this.updateState(response.data.metrics, response.data.groups);
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "Metric Updated"
                    });
                } catch (error) {
                    this.errors = error.response.data.errors;
                }
            },

            loadMetricData() {
                this.loading = true;
                axios.post('/api/map/cresiteiq_data/by-area', {
                    area_type: this.selectedMetric.granularity == 'state' ? 'App\\State' : 'App\\Zip',
                    metric_id: this.selectedMetric.id
                }).then(response => {
                    let metric = _.find(this.allMetrics, ['id', this.selectedMetricId])
                    metric.data = response.data;

                    this.$forceUpdate();
                }).finally(() => {
                    this.loading = false;   
                }).catch(error => {
                    this.errors = error.response.data.errors;
                });

            },

            // async addSelectedMetrics() {
            //     try {
            //         const response = await axios.patch(`/admin/cresiteiq/add-metric-group`, this.getSelectedMetrics('available_metrics'));
            //         this.updateState(response.data.metrics, response.data.groups);
            //         this.$notify({
            //             type: 'success',
            //             title: 'Success',
            //             text: "Metrics Updated"
            //         });
            //     } catch (error) {
            //         this.errors = error.response.data.errors;
            //     }
            // },
            async addSelectedMetrics() {
                try {
                    // Correctly get the data from your Vue component's state
                    // The `selectedMetrics` array already contains the objects you need.
                    const metricsToAdd = this.creSiteIQ.selectedMetrics.map(metric => ({
                        slug: metric.slug,
                        group_id: this.selectedGroup.id
                    }));

                    const response = await axios.patch(`/admin/cresiteiq/add-metric-group`, metricsToAdd);
                    
                    // Ensure you update your state after a successful API call
                    this.updateState(response.data.metrics, response.data.groups);
                    this.creSiteIQ.selectedMetrics = [];
                    this.searchCriteria = '';
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "Metrics Updated"
                    });
                } catch (error) {
                    this.errors = error.response.data.errors;
                }
            },

            async removeSelectedMetrics() {
                try {
                    const metricsToRemove = this.creSiteIQ.selectedMetrics.map(metric => ({
                        slug: metric.slug,
                        group_id: this.selectedGroup.id
                    }));
                    const response = await axios.patch(`/admin/cresiteiq/remove-metric-group`, metricsToRemove);
                    this.updateState(response.data.metrics, response.data.groups);
                    this.creSiteIQ.selectedMetrics = [];
                    this.searchCriteria = '';
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "Metrics Updated"
                    });
                } catch (error) {
                    this.errors = error.response.data.errors;
                }
            },

            async deleteGroup() {
                try {
                    const response = await axios.delete(`/admin/cresiteiq/metric-group/${this.selectedGroup.id}`);
                    this.updateState(response.data.metrics, response.data.groups);
                    this.selectedGroupSlug = '';
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "Group Deleted"
                    });
                } catch (error) {
                    this.errors = error.response.data.errors;
                }
            },

            async uploadData() {
                this.uploadProgress = 1;
                this.uploadSuccessful = false;
                this.errors = {};
                this.isUploadingFile = false;
                this.isProcessingOnServer = false;
                this.uploadMessage = '';

                if (!this.uploadedDataType) {
                    this.errors = { upload: ['You must select a data type'] };
                    return;
                }

                if (!this.uploadedFile) {
                    this.errors = { upload: ['You must select a file to upload'] };
                    return;
                }

                this.loading = true;
                this.isUploadingFile = true;
                let intervalId = null;

                // --- Calculate dynamic processing speed based on file size ---
                const fileSizeMB = this.uploadedFile.size / (1024 * 1024); // Convert bytes to MB

                // Define a base interval and how much it should slow down per MB
                const baseIntervalMs = 400; // Faster update for small files
                const slowdownFactorPerMB = 15; // How much to increase interval per MB
                const maxIntervalMs = 2500; // Max interval to prevent infinite slowness

                // Calculate processing interval
                let processingIntervalMs = Math.min(
                    maxIntervalMs,
                    baseIntervalMs + (fileSizeMB * slowdownFactorPerMB)
                );
                processingIntervalMs = Math.max(200, processingIntervalMs); // Minimum interval

                // --- Define progress bar phases ---
                const uploadPhaseMax = 20; // File upload will cover 1% to 70%
                const processingPhaseMax = 95; // Processing will smoothly go from 70% up to 99.5%

                await new Promise(resolve => setTimeout(resolve, 100));

                await axios.post('/admin/cresiteiq/data',
                    {
                        type: this.uploadedDataType,
                        file: this.uploadedFile
                    },
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        },
                        onUploadProgress: (progressEvent) => {
                    const uploadedPercentage = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );

                    // Update actual upload progress
                    let currentUploadProgress = 1 + (uploadedPercentage / 100) * (uploadPhaseMax - 1);
                    this.uploadProgress = Math.min(currentUploadProgress, uploadPhaseMax);

                    // Transition to server processing phase
                    if (uploadedPercentage === 100 && !this.isProcessingOnServer) {
                        this.isUploadingFile = false; // File upload phase is complete
                        this.isProcessingOnServer = true; // Now in server processing phase

                        // Ensure we start the processing phase from uploadPhaseMax if not already there
                        if (this.uploadProgress < uploadPhaseMax) {
                            this.uploadProgress = uploadPhaseMax;
                        }

                        // Start incremental progress for server processing.
                        // This will continue to run until API response comes.
                        intervalId = setInterval(() => {
                            // Only increment if not yet at the processingPhaseMax limit
                            if (this.uploadProgress < processingPhaseMax) {
                                // Calculate how much to increment.
                                // It should slow down as it approaches processingPhaseMax.
                                const remainingProgress = processingPhaseMax - this.uploadProgress;

                                // Increment by a diminishing amount, but never less than 0.05%
                                const increment = Math.max(0.05, remainingProgress / 20);
                                this.uploadProgress += increment;

                                // Cap the progress at processingPhaseMax
                                this.uploadProgress = Math.min(this.uploadProgress, processingPhaseMax);
                            }
                            // console.log(`Processing: ${this.uploadProgress.toFixed(2)}%`); // For debugging
                        }, processingIntervalMs); // Use the dynamically calculated interval
                    }
                }



                    }
                ).then(response => {
                    this.uploadMessage = response.data.message;
                    this.uploadSuccessful = true;
                    this.uploadedFile = null;
                    this.loading = false;
                    this.isUploadingFile = false;
                    this.isProcessingOnServer = false;
                    this.uploadProgress = 100;  
                    this.isProcessing = false; // Now in processing phase

                    if (intervalId) {
                        clearInterval(intervalId);
                    }

                    setTimeout(() => {
                        this.uploadProgress = 0;
                        this.loading = false;
                        this.isProcessingOnServer = false;
                    }, 1000);
                    // Keep it visible for 1 second after success

                }).catch(error => {
                    this.errors = { upload: error.response.data.errors };
                    this.loading = false;

                }).finally(() => {
                    this.loading = false;
                });
            },

            async uploadMatrixData() {
                this.uploadSuccessful = false;
                this.errors = {};
                
                if (!this.matrixUploadedFile) {
                    this.errors = { upload: ['You must select a file to upload'] };
                    return;
                }
                this.loading= true;

                await axios.post('/admin/cresiteiq/bulkUpload',
                    {
                        file: this.matrixUploadedFile
                    },
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                ).then(response => {
                    this.uploadMessage = response.data.message;
                    this.uploadSuccessful = true;
                    this.matrixUploadedFile = null;
                    this.loading=false;
                }).catch(error => {
                    this.errors = { upload: error.response.data.errors };
                    this.loading=false;

                }).finally(() => {
                    this.loading = false;
                });
            },

            updatePoiSetting(value) {
                this.loading = true;
                axios.post('/admin/update-poi-setting', {
                    poi_enabled: value
                }).then(response => {
                    this.poiEnabled = value;
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "POI setting updated successfully"
                    });
                }).catch(error => {
                    console.error('Error updating POI setting:', error);
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
                }).finally(() => {
                    this.loading = false;
                });
            },

            // Helper Methods
            getSelectedMetrics(selectId) {
                const selectedMetrics = document.getElementById(selectId);
                console.log(selectedMetrics.selectedOptions,'selectedId');
                return _.map(selectedMetrics.selectedOptions, row => ({
                    slug: row.value,
                    group_id: this.selectedGroup.id
                }));
            },

            updateState(metrics, groups) {
                this.allMetrics = metrics;
                this.groups = groups;
            },

            onUploadFileChanged(event) {
                const target = event.target;
                if (target?.files) {
                    this.uploadedFile = target.files[0];
                    this.matrixUploadedFile = target.files[0];
                    console.log("uploaded matrix file", this.matrixUploadedFile)
                }
            },

            confirmDeleteGroup() {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this group?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteGroup();
                    }
                });
            }
        },
        mounted() {



            axios.get('/admin/get-poi-setting')
            .then(response => {
                console.log('POI Setting Response:', response.data);
                // Convert string '0'/'1' to boolean
                this.poiEnabled = response.data.poi_enabled === '1' || response.data.poi_enabled === true;
            })
            .catch(error => {
                console.error('Error fetching POI setting:', error);
            });


        },
        
    }
</script>
